<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRouteTabs } from 'dashboard/composables/useRouteTabs';
import RouteTabs from 'dashboard/components-next/tabbar/RouteTabs.vue';
import TabsPageHeader from 'dashboard/components-next/tabbar/TabsPageHeader.vue';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const buildTab = (key, label, icon, routeName) => ({
  key,
  label,
  icon,
  to: accountScopedRoute(routeName),
  activeOn: [routeName],
});

const triggerTabs = computed(() => [
  buildTab(
    'follow_ups',
    t('AUTOMATIONS.TABS.FOLLOW_UPS'),
    'i-lucide-message-square-reply',
    'automations_follow_ups'
  ),
  buildTab(
    'reminders',
    t('AUTOMATIONS.TABS.REMINDERS'),
    'i-lucide-calendar-clock',
    'automations_reminders'
  ),
]);

const internalTabs = computed(() => [
  buildTab(
    'rules',
    t('AUTOMATIONS.TABS.RULES'),
    'i-lucide-workflow',
    'automations_rules'
  ),
  buildTab(
    'macros',
    t('AUTOMATIONS.TABS.MACROS'),
    'i-lucide-toy-brick',
    'automations_macros'
  ),
  buildTab(
    'canned',
    t('AUTOMATIONS.TABS.CANNED'),
    'i-lucide-message-square-quote',
    'automations_canned'
  ),
]);

const triggers = useRouteTabs(triggerTabs);
const internal = useRouteTabs(internalTabs);

const groups = computed(() =>
  [
    {
      key: 'triggers',
      label: t('AUTOMATIONS.TABS.TRIGGERS'),
      icon: 'i-lucide-send',
      tabs: triggers.visibleTabs.value,
      activeTab: triggers.activeTab.value,
    },
    {
      key: 'internal',
      label: t('AUTOMATIONS.TABS.INTERNAL'),
      icon: 'i-lucide-command',
      tabs: internal.visibleTabs.value,
      activeTab: internal.activeTab.value,
    },
  ]
    .filter(group => group.tabs.length > 0)
    .map(group => ({ ...group, to: group.tabs[0].to }))
);

const activeGroup = computed(() => groups.value.find(group => group.activeTab));

// Landing on /automations: go to the first accessible tab, in display order
const defaultTab = computed(
  () => [...triggers.visibleTabs.value, ...internal.visibleTabs.value][0]
);

watch(
  [() => route.name, defaultTab],
  ([routeName, tab]) => {
    if (routeName === 'automations_home' && tab) router.replace(tab.to);
  },
  { immediate: true }
);
</script>

<template>
  <div
    class="flex flex-col flex-1 h-full m-0 overflow-auto bg-n-background px-6"
  >
    <div class="flex flex-col w-full max-w-[60rem] mx-auto gap-6 pb-8">
      <div class="flex flex-col gap-4">
        <TabsPageHeader
          :title="t('AUTOMATIONS.HEADER')"
          :help-url="t('AUTOMATIONS.HELP_URL')"
        />
        <RouteTabs :tabs="groups" :active-key="activeGroup?.key" />
      </div>
      <div v-if="activeGroup" class="flex flex-col">
        <RouteTabs
          variant="folder"
          :tabs="activeGroup.tabs"
          :active-key="activeGroup.activeTab.key"
        />
        <div
          class="p-5 border rounded-b-lg rounded-tr-lg border-n-weak bg-n-solid-1"
        >
          <router-view />
        </div>
      </div>
    </div>
  </div>
</template>
